import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './AssignmentSubmit.css';

const AssignmentSubmit = () => {
  const { assignmentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [assignment, setAssignment] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [textContent, setTextContent] = useState('');
  const [comments, setComments] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch assignment details
        const assignmentResponse = await api.get(`/assignments/${assignmentId}`);
        setAssignment(assignmentResponse.data);

        // Fetch existing submission
        const submissionResponse = await api.get(`/assignments/${assignmentId}/my-submission`);
        if (submissionResponse.data) {
          setSubmission(submissionResponse.data);
          setTextContent(submissionResponse.data.text_content || '');
          setComments(submissionResponse.data.comments || '');
        }
      } catch (err) {
        console.error('Error fetching assignment:', err);
        setError('Failed to load assignment details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [assignmentId]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size
      if (assignment.max_file_size && file.size > assignment.max_file_size) {
        setError(`File too large. Maximum size: ${formatFileSize(assignment.max_file_size)}`);
        return;
      }

      // Check file type
      if (assignment.file_types_allowed) {
        const allowedTypes = assignment.file_types_allowed.split(',').map(t => t.trim().toLowerCase());
        const fileExt = file.name.split('.').pop().toLowerCase();
        if (!allowedTypes.includes(fileExt)) {
          setError(`Invalid file type. Allowed types: ${assignment.file_types_allowed}`);
          return;
        }
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  const handleTextSubmit = async (e) => {
    e.preventDefault();

    if (!textContent.trim()) {
      setError('Please enter your submission text');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const response = await api.post(`/assignments/${assignmentId}/submit-text`, {
        text_content: textContent,
        comments: comments,
      });

      setSubmission(response.data);
      setSuccess('Assignment submitted successfully!');
    } catch (err) {
      console.error('Error submitting assignment:', err);
      setError(err.response?.data?.error || 'Failed to submit assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', selectedFile);
      if (comments) {
        formData.append('comments', comments);
      }

      const response = await api.post(
        `/uploads/assignment/${assignmentId}/submit`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setSubmission(response.data);
      setSuccess('File uploaded successfully!');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.response?.data?.error || 'Failed to upload file');
    } finally {
      setSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) return <div className="loading">Loading assignment...</div>;
  if (error && !assignment) return <div className="error-state">{error}</div>;

  const isGraded = submission?.status === 'graded';

  return (
    <div className="page assignment-submit-page">
      <div className="container">
        <section className="assignment-header">
          <div className="header-content">
            <Link to={`/courses/${assignment?.course_id}/assignments`} className="back-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 19l-7-7 7-7" />
              </svg>
              Back to Assignments
            </Link>
            <p className="course-title">{assignment?.course_title}</p>
            <h1>{assignment?.title}</h1>
          </div>
          <div className="header-meta">
            <div className="meta-card">
              <span className="label">Max Score</span>
              <p className="value">{assignment?.max_score}</p>
            </div>
            {submission && (
              <div className={`status-card ${submission.status}`}>
                <span className="label">Status</span>
                <p className="value">{submission.status}</p>
              </div>
            )}
          </div>
        </section>

        <div className="assignment-content">
          {/* Assignment Instructions */}
          <section className="instructions-section">
            <h2>Instructions</h2>
            <div className="instructions-content">
              {assignment?.instructions || assignment?.description || 'No instructions provided.'}
            </div>

            <div className="assignment-details">
              {assignment?.due_days && (
                <div className="detail-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                  <span>Due: {assignment.due_days} days after enrollment</span>
                </div>
              )}
              {assignment?.file_types_allowed && (
                <div className="detail-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <path d="M14 2v6h6M12 18v-6M9 15h6" />
                  </svg>
                  <span>Allowed files: {assignment.file_types_allowed}</span>
                </div>
              )}
              {assignment?.max_file_size && (
                <div className="detail-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                  </svg>
                  <span>Max file size: {formatFileSize(assignment.max_file_size)}</span>
                </div>
              )}
              {assignment?.allow_late && (
                <div className="detail-item success">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Late submissions allowed</span>
                </div>
              )}
            </div>
          </section>

          {/* Grade Display (if graded) */}
          {isGraded && (
            <section className="grade-section">
              <h2>Your Grade</h2>
              <div className="grade-card">
                <div className="grade-score">
                  <span className="score">{submission.score}</span>
                  <span className="max-score">/ {assignment.max_score}</span>
                  <span className={`percentage ${(submission.score / assignment.max_score) >= 0.7 ? 'passing' : 'failing'}`}>
                    ({Math.round((submission.score / assignment.max_score) * 100)}%)
                  </span>
                </div>
                {submission.feedback && (
                  <div className="feedback">
                    <h4>Instructor Feedback</h4>
                    <p>{submission.feedback}</p>
                  </div>
                )}
                <div className="grade-meta">
                  <span>Graded on: {formatDate(submission.graded_at)}</span>
                  {submission.grader_name && (
                    <span>By: {submission.grader_name}</span>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Existing Submission Display */}
          {submission && !isGraded && (
            <section className="submission-section">
              <h2>Your Submission</h2>
              <div className="submission-card">
                <div className="submission-meta">
                  <span>Submitted: {formatDate(submission.submitted_at)}</span>
                </div>
                {submission.text_content && (
                  <div className="submission-text">
                    <h4>Text Submission</h4>
                    <p>{submission.text_content}</p>
                  </div>
                )}
                {submission.file_name && (
                  <div className="submission-file">
                    <h4>Uploaded File</h4>
                    <div className="file-info">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                        <path d="M14 2v6h6" />
                      </svg>
                      <span>{submission.file_name}</span>
                      {submission.file_size && (
                        <span className="file-size">({formatFileSize(submission.file_size)})</span>
                      )}
                    </div>
                  </div>
                )}
                {submission.comments && (
                  <div className="submission-comments">
                    <h4>Your Comments</h4>
                    <p>{submission.comments}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Alerts */}
          {error && (
            <div className="alert alert-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M15 9l-6 6M9 9l6 6" />
              </svg>
              {error}
            </div>
          )}
          {success && (
            <div className="alert alert-success">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {success}
            </div>
          )}

          {/* Submission Forms */}
          {(!isGraded || assignment?.allow_late) && (
            <section className="submit-section">
              <h2>{submission ? 'Update Submission' : 'Submit Assignment'}</h2>

              <div className="submission-tabs">
                <div className="tab-content">
                  {/* Text Submission */}
                  <form onSubmit={handleTextSubmit} className="submit-form">
                    <div className="form-group">
                      <label htmlFor="textContent">Your Answer</label>
                      <textarea
                        id="textContent"
                        value={textContent}
                        onChange={(e) => setTextContent(e.target.value)}
                        placeholder="Type your answer here..."
                        rows={8}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="comments">Additional Comments (Optional)</label>
                      <textarea
                        id="comments"
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder="Any additional notes for the instructor..."
                        rows={3}
                      />
                    </div>

                    <button type="submit" className="btn-primary" disabled={submitting}>
                      {submitting ? 'Submitting...' : (submission ? 'Update Submission' : 'Submit Answer')}
                    </button>
                  </form>

                  <div className="divider">
                    <span>OR</span>
                  </div>

                  {/* File Submission */}
                  <form onSubmit={handleFileSubmit} className="submit-form file-form">
                    <div className="form-group">
                      <label>Upload File</label>
                      <div className="file-upload-area" onClick={() => fileInputRef.current?.click()}>
                        <input
                          ref={fileInputRef}
                          type="file"
                          onChange={handleFileChange}
                          accept={assignment?.file_types_allowed?.split(',').map(t => `.${t.trim()}`).join(',')}
                        />
                        {selectedFile ? (
                          <div className="selected-file">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                              <path d="M14 2v6h6" />
                            </svg>
                            <span>{selectedFile.name}</span>
                            <span className="file-size">({formatFileSize(selectedFile.size)})</span>
                          </div>
                        ) : (
                          <div className="upload-placeholder">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                            </svg>
                            <span>Click to select a file or drag and drop</span>
                            {assignment?.file_types_allowed && (
                              <span className="file-types">Allowed: {assignment.file_types_allowed}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="fileComments">Comments (Optional)</label>
                      <textarea
                        id="fileComments"
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder="Any additional notes for the instructor..."
                        rows={3}
                      />
                    </div>

                    <button type="submit" className="btn-primary" disabled={submitting || !selectedFile}>
                      {submitting ? 'Uploading...' : 'Upload File'}
                    </button>
                  </form>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignmentSubmit;
